import {loginPath} from "../lib/clinicSignIn";
import React, {useEffect, useState, useReducer, useRef, useCallback} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {verifyUser} from "../authRequests";
import {patientReducer, init} from "../additional_files/reducer";
import Layout from "../components/Layout";
import {
  Box,
  VStack,
  Heading,
  Button,
  useToast,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Icon,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import {FiUpload, FiFolder, FiSearch, FiX} from "react-icons/fi";
import PatientFileBrowser from "../components/Files/PatientFileBrowser";
import PatientSelectedControls from "../components/Files/PatientSelectedControls";
import PreviewFile from "../components/Files/PreviewFile";
import UploadModal from "../components/Files/UploadModal";
import {crudStorage} from "../components/Files/helpers/crudStorage";

export default function Files() {
  const navigate = useNavigate();
  const {state} = useLocation();
  const loadedRef = useRef(null);
  const [dashState, dispatch] = useReducer(patientReducer, state, init);
  const toast = useToast();

  // Files state - breadcrumb navigation model
  const [folderItems, setFolderItems] = useState([]);
  const [currentPath, setCurrentPath] = useState("");
  const [isNavigating, setIsNavigating] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");
  const [selectedPath, setSelectedPath] = useState("");
  const [preview, setPreview] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const isLoading = false;
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFile, setDroppedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const searchTimeoutRef = useRef(null);
  const searchAbortRef = useRef(false);

  // Upload modal
  const { isOpen: isUploadOpen, onOpen: onUploadOpen, onClose: onUploadClose } = useDisclosure();

  // Root path is always patient ID root
  const rootPath = dashState?.patient?.pid ? `${dashState.patient.pid}/` : "";

  useEffect(() => {
    verifyUser(state ? state.jwt : "", loadedRef);
  }, []);

  useEffect(() => {
    if (!state || !state.patient) {
      navigate(loginPath());
    }
  }, [state, navigate]);

  // Initialize currentPath and load root on mount
  useEffect(() => {
    if (rootPath && state?.patient) {
      setCurrentPath(rootPath);
      navigateToFolder(rootPath);
    }
  }, [rootPath]);

  const navigateToFolder = useCallback(async (folderPath) => {
    if (!folderPath) return;

    setIsNavigating(true);
    setSelectedFile("");
    setSelectedPath("");
    try {
      const res = await crudStorage(
        dashState,
        "view",
        "",
        dashState.organization?.s3BucketName || "brightlighthealth",
        folderPath
      );
      setFolderItems(res.data || []);
      setCurrentPath(folderPath);
    } catch (error) {
      console.error("Error navigating to folder:", error);
      setFolderItems([]);
      toast({
        title: "Error loading folder",
        description: "Could not load folder contents",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsNavigating(false);
    }
  }, [dashState, toast]);

  const fastSearch = useCallback(async (query, maxResults = 25) => {
    if (!rootPath) return [];
    const results = [];
    const visited = new Set();
    searchAbortRef.current = false;

    const searchFolder = async (folderPath) => {
      if (searchAbortRef.current || results.length >= maxResults) return;
      if (visited.has(folderPath)) return;
      visited.add(folderPath);

      try {
        const res = await crudStorage(
          dashState,
          "view",
          "",
          dashState.organization?.s3BucketName || "brightlighthealth",
          folderPath
        );

        const items = res.data || [];
        const subfolders = [];

        for (const item of items) {
          if (!item.name || searchAbortRef.current) continue;

          const itemPath = folderPath + item.name;
          const isFolder = item.name.endsWith("/");
          const itemName = item.name.replace(/\/$/, "").toLowerCase();

          if (itemName.includes(query)) {
            results.push({
              name: item.name,
              displayName: item.displayName,
              path: itemPath,
              isFolder,
            });

            if (results.length <= maxResults) {
              setSearchResults([...results]);
            }

            if (results.length >= maxResults) return;
          }

          if (isFolder) subfolders.push(itemPath);
        }

        if (!searchAbortRef.current && results.length < maxResults) {
          const chunks = [];
          for (let i = 0; i < subfolders.length; i += 3) {
            chunks.push(subfolders.slice(i, i + 3));
          }
          for (const chunk of chunks) {
            if (searchAbortRef.current || results.length >= maxResults) break;
            await Promise.all(chunk.map((folder) => searchFolder(folder)));
          }
        }
      } catch {
        // Continue search through other branches
      }
    };

    await searchFolder(rootPath);
    return results;
  }, [dashState, rootPath]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchAbortRef.current = true;

    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setSearchResults([]);

    searchTimeoutRef.current = setTimeout(async () => {
      const query = searchQuery.trim().toLowerCase();
      searchAbortRef.current = false;
      await fastSearch(query, 25);
      if (!searchAbortRef.current) setIsSearching(false);
    }, 150);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchAbortRef.current = true;
    };
  }, [searchQuery, fastSearch]);

  const handleSearchResultClick = (result) => {
    if (result.isFolder) {
      navigateToFolder(result.path);
    } else {
      const parentPath = result.path.substring(0, result.path.lastIndexOf("/") + 1);
      navigateToFolder(parentPath).then(() => {
        setSelectedPath(parentPath);
        setSelectedFile(result.name);
      });
    }
    setSearchQuery("");
  };

  // Callback for successful upload
  const handleUploadSuccess = async () => {
    // Navigate to Client Uploads folder to show the new file
    const clientUploadsPath = rootPath + "Client Uploads/";
    await navigateToFolder(clientUploadsPath);
  };

  // Callback for successful deletion
  const handleDeleteSuccess = async () => {
    await navigateToFolder(currentPath);
  };

  // Drag and drop handlers for OS file upload
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    const hasInternalData = e.dataTransfer.types.includes('text/plain') ||
                           e.dataTransfer.types.includes('text/html');

    if (droppedFiles && droppedFiles.length > 0 && !hasInternalData) {
      setDroppedFile(droppedFiles[0]);
      onUploadOpen();
    }
  };

  const handleModalClose = () => {
    setDroppedFile(null);
    onUploadClose();
  };

  return (
    <Layout state={{...dashState}} dispatch={dispatch}>
      <Box
        ref={loadedRef}
        style={{opacity: 0}}
        w="100%"
        flex={1}
        display="flex"
        flexDirection="column"
      >
        <Box w="100%" flex={1} px={{base: 4, md: 6}} py={5} display="flex" flexDirection="column">
        <VStack align="stretch" spacing={6} flex={1}>
          {/* Header */}
          <Box>
            <Heading fontSize="xl" fontWeight="semibold" mb={4} color="gray.800">
              Files
            </Heading>
            <Text fontSize="sm" color="gray.500" mt={-2}>
              View documents shared by your provider and upload files to Client Uploads folder
            </Text>
          </Box>

          {/* Search bar and Upload button */}
          <Flex justify="space-between" align="center">
            <InputGroup maxW="400px">
              <InputLeftElement pointerEvents="none">
                {isSearching ? (
                  <Spinner size="sm" color="blue.500" />
                ) : (
                  <Icon as={FiSearch} color="gray.400" />
                )}
              </InputLeftElement>
              <Input
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="sm"
                borderRadius="8px"
                borderColor="rgba(0,0,0,0.08)"
                _hover={{borderColor: "rgba(0,0,0,0.15)"}}
                _focus={{borderColor: "gray.400", boxShadow: "0 0 0 1px var(--chakra-colors-gray-400)"}}
                pr={searchQuery ? "40px" : "12px"}
              />
              {searchQuery && (
                <InputRightElement>
                  <IconButton
                    aria-label="Clear search"
                    icon={<FiX />}
                    size="sm"
                    variant="ghost"
                    color="gray.400"
                    _hover={{color: "gray.600"}}
                    onClick={() => setSearchQuery("")}
                  />
                </InputRightElement>
              )}
            </InputGroup>

            <Button
              leftIcon={<FiUpload />}
              bg="#1a1a1a"
              color="white"
              _hover={{bg: "#333"}}
              size="sm"
              onClick={onUploadOpen}
              cursor="pointer"
              borderRadius="8px"
              fontSize="13px"
              fontWeight="500"
            >
              Upload File
            </Button>
          </Flex>

          {/* Selected file controls */}
          {selectedFile && (
            <PatientSelectedControls
              selectedFile={selectedFile}
              path={selectedPath}
              setFiles={(files) => setFolderItems(files)}
              setSelectedFile={setSelectedFile}
              preview={preview}
              setPreview={setPreview}
              toast={toast}
              dashState={dashState}
              onDeleteSuccess={handleDeleteSuccess}
            />
          )}

          {/* File browser */}
          <Box
            border={isDragging ? "2px dashed" : "1px solid"}
            borderColor={isDragging ? "#1a1a1a" : "rgba(0,0,0,0.06)"}
            borderRadius="10px"
            p={4}
            minH="500px"
            bg={isDragging ? "gray.50" : "rgba(250,250,250,0.3)"}
            transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            position="relative"
            sx={{
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: '3px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.15)' },
            }}
          >
            {isLoading && folderItems.length === 0 ? (
              <Flex justify="center" align="center" h="300px">
                <Text color="gray.500">Loading files...</Text>
              </Flex>
            ) : !isNavigating && folderItems.length === 0 && currentPath === rootPath ? (
              <Flex justify="center" align="center" h="300px" direction="column">
                <Icon as={FiFolder} fontSize="48px" color="gray.300" mb={4} />
                <Text color="gray.500" mb={2}>No files available</Text>
                <Text color="gray.400" fontSize="sm">
                  Files shared by your provider will appear here
                </Text>
              </Flex>
            ) : (
              <PatientFileBrowser
                folderItems={folderItems}
                currentPath={currentPath}
                rootPath={rootPath}
                onNavigate={navigateToFolder}
                isNavigating={isNavigating}
                dashState={dashState}
                setSelectedPath={setSelectedPath}
                setSelectedFile={setSelectedFile}
                selectedPath={selectedPath}
                selectedFile={selectedFile}
                setPreview={setPreview}
                toast={toast}
                selectedFiles={selectedFiles}
                setSelectedFiles={setSelectedFiles}
                searchQuery={searchQuery}
                searchResults={searchResults}
                isSearching={isSearching}
                onSearchResultClick={handleSearchResultClick}
              />
            )}
          </Box>
        </VStack>

        {/* Preview modal */}
        <PreviewFile
          preview={preview}
          setPreview={setPreview}
        />

        {/* Upload modal */}
        <UploadModal
          isOpen={isUploadOpen}
          onClose={handleModalClose}
          dashState={dashState}
          onSuccess={handleUploadSuccess}
          initialFile={droppedFile}
        />
        </Box>
      </Box>
    </Layout>
  );
}
